import { ComponentType } from 'react';
import type { ConfigType } from '@plone/registry';
import type { SettingsConfig } from '@plone/types';
import { contentIcons } from './contentIcons';
import { customUrlValidator } from '../helpers/FromValidation/customUrlValidator';
import ModuleView from '../components/Views/ModuleView';
import OccasionView from '../components/Views/OccasionView';
import DownloadRedirectView from '../components/Views/DownloadRedirectView';
import ServerError from '../components/Error/ServerError';
import ConnectionRefused from '../components/Error/ConnectionRefused';
import CorsError from '../components/Error/CorsError';
import RequestTimeout from '../components/Error/RequestTimeout';
import Unauthorized from '../components/Error/Unauthorized';
import Forbidden from '../components/Error/Forbidden';
import NotFound from '../components/Error/NotFound';
import { styleClassNameExtender } from '../components/Blocks/BlockWrapper/BlockWrapper';

/**
 * Configure application settings
 * Follows SOLID principles by separating settings configuration
 */
export const configureSettings = (config: ConfigType) => {
  // Environment configuration
  const env = (
    typeof window !== 'undefined' ? (window as any) : (process as any)
  )?.env;
  config.settings['FHNW_ENV'] = env?.RAZZLE_FHNW_ENV || 'NOENV';

  // Main settings configuration
  config.settings = {
    ...config.settings,
    navDepth: 4,
    querystringSearchGet: true,
    isMultilingual: true,
    supportedLanguages: ['de', 'en', 'fr'],
    defaultLanguage: 'de',
    // Languages that must not be offered on a language root (`/de`, `/en`,
    // `/fr`): there is no French homepage, only single translated sections,
    // so the language roots link to each other without a page to show.
    languagesHiddenOnLanguageRoot: ['fr'],
    cookiebotDomainGroupId: '1346d074-2668-4a7b-8f47-624c2786b205',
    gaMeasurementId: 'GTM-KDPH6Q5',
  } as SettingsConfig;

  // Content icons configuration (SVG paths as strings)
  (config.settings as any).contentIcons = {
    ...(config.settings as any).contentIcons,
    ...contentIcons,
  };

  // Working copy support
  config.settings.hasWorkingCopySupport = true;

  // API expanders configuration
  config.settings.apiExpanders = [
    ...config.settings.apiExpanders,
    {
      match: '',
      GET_CONTENT: ['intro_bubble', 'metanav'],
    },
    {
      match: '',
      GET_CONTENT: ['navtree'],
      querystring: {
        'expand.navtree.depth': '3',
      },
    },
  ];

  // External routes
  config.settings.externalRoutes = [{ match: '/plattformen' }];

  // Object browser configuration
  config.settings.objectBrowser = config.settings.objectBrowser || {};
  (
    config.settings.objectBrowser as {
      initialPathFn?: {
        [key: string]: (path: string, lang: string) => string | undefined;
      };
    }
  ).initialPathFn = {
    ...(
      config.settings.objectBrowser as {
        initialPathFn?: {
          [key: string]: (path: string, lang: string) => string | undefined;
        };
      }
    ).initialPathFn,
    contact: (_path: string, lang: string) =>
      ({ de: '/de', fr: '/fr', en: '/en' })[lang] || '/de',
    location: (_path: string, lang: string) =>
      ({ de: '/de/orte', fr: '/fr/lieux', en: '/en/locations' })[lang] ||
      '/de/orte',
  };

  // Form validation
  config.settings.formValidation = {
    ...(config.settings.formValidation || {}),
    customUrlValidator,
  };

  // Excluded fields from automatic translations
  config.settings.translationFieldExcludes = [
    'UID',
    'id',
    'effective',
    'expires',
    'review_state',
    'blocks',
    'blocks_layout',
    'related_contacts',
    'related_locations',
    'preview_image_link',
    'hero_image',
    'opengraph_image',
    'link',
    'documents',
    'creators',
    'contributors',
    'has_todo',
    'completed_todo',
    'amendment_note',
  ];

  // Style class name extenders
  config.settings.styleClassNameExtenders = [styleClassNameExtender];

  return config;
};

/**
 * Configure views for different content types
 */
export const configureViews = (config: ConfigType) => {
  // Content type views
  config.views.contentTypesViews = {
    ...config.views.contentTypesViews,
    Module: ModuleView,
    Occasion: OccasionView,
    File: DownloadRedirectView,
    Image: DownloadRedirectView,
  } as Record<string, ComponentType<any>>;

  // Error views
  config.views.errorViews = {
    ...config.views.errorViews,
    '401': Unauthorized,
    '403': Forbidden,
    '404': NotFound,
    '408': RequestTimeout,
    '500': ServerError,
    ECONNREFUSED: ConnectionRefused,
    corsError: CorsError,
  };

  return config;
};

/**
 * Configure utilities
 */
export const configureUtilities = (config: ConfigType) => {
  config.registerUtility({
    name: 'url',
    type: 'validator',
    dependencies: { widget: 'url' },
    method: customUrlValidator,
  });

  return config;
};
